import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronRight } from '@/assets/icons'
import { kExternalStorageProviderDisplayName } from '@/constants/constants-external-storage'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { MatterSettingsConnectedAccountCard } from '@/matter/components/matter-settings/MatterSettingsConnectedAccountCard'
import { selectMatterById } from '@/matter/store/selectors'
import { matterPaths } from '@/routing/paths'
import { pickVisibleAccount } from '@/settings/utils/pick-visible-account'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { SelectExternalFolderModal } from '@/user_files/external-storage/components/SelectExternalFolderModal'
import { DisconnectDialog } from '@/user_files/external-storage/components/DisconnectDialog'
import { deleteMatterExternalSyncMapping } from '@/user_files/external-storage/fetch/delete-matter-external-sync-mapping'
import {
  ExternalStorageActions,
  kExternalStorageRequestStatus,
  selectConnectedAccounts,
  selectExternalStorageAccountsRequest,
  selectMatterExternalSyncMapping,
  selectMatterExternalSyncMappingRequest,
} from '@/user_files/external-storage/store/slice'

export default function MatterSettingsPage() {
  const { matterId } = useParams<{ matterId: string }>()
  const dispatch = useAppDispatch()
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))
  const accounts = useAppSelector(selectConnectedAccounts)
  const accountsRequest = useAppSelector(selectExternalStorageAccountsRequest)
  const syncMapping = useAppSelector((state) => selectMatterExternalSyncMapping(state, matterId))
  const mappingRequest = useAppSelector((state) => selectMatterExternalSyncMappingRequest(state, matterId))
  const [selectFolderOpen, setSelectFolderOpen] = useState(false)
  const [disconnectFolderDialogOpen, setDisconnectFolderDialogOpen] = useState(false)
  const [disconnectingFolder, setDisconnectingFolder] = useState(false)

  useEffect(() => {
    if (accountsRequest.status === kExternalStorageRequestStatus.IDLE) {
      dispatch(ExternalStorageActions.fetchConnectedAccounts())
    }
  }, [accountsRequest.status, dispatch])

  useEffect(() => {
    if (!matterId) return

    if (mappingRequest.status === kExternalStorageRequestStatus.IDLE) {
      dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping(matterId))
    }
  }, [dispatch, mappingRequest.status, matterId])

  const handleFolderImportSuccess = () => {
    if (!matterId) return

    dispatch(ExternalStorageActions.fetchMatterExternalSyncMapping(matterId))
  }

  const handleFolderDisconnect = async () => {
    if (!matterId || !syncMapping) return

    setDisconnectingFolder(true)
    try {
      await deleteMatterExternalSyncMapping(matterId)
      dispatch(ExternalStorageActions.matterSyncMappingDisconnected({ matterId }))
      setDisconnectFolderDialogOpen(false)
      dispatch(
        openGlobalToast({
          type: GlobalToastType.SUCCESS,
          message: 'Synced folder disconnected.',
          durationMs: kToastDurationShortMs,
        })
      )
    } catch {
      dispatch(
        openGlobalToast({
          type: GlobalToastType.ERROR,
          message: 'Failed to disconnect synced folder. Please try again.',
          durationMs: kToastDurationShortMs,
        })
      )
    } finally {
      setDisconnectingFolder(false)
    }
  }

  const connectedAccounts = useMemo(() => accounts.filter((account) => account.status === 'connected'), [accounts])
  const visibleAccount = useMemo(() => pickVisibleAccount(connectedAccounts), [connectedAccounts])
  const activeSyncMapping = syncMapping?.status === 'active' ? syncMapping : null
  const syncAccount = activeSyncMapping ? accounts.find((account) => account.id === activeSyncMapping.account_id) ?? null : visibleAccount
  const canChooseFolder = syncAccount?.status === 'connected'
  const syncedFolderName = useMemo(
    () => activeSyncMapping?.external_folder_name ?? activeSyncMapping?.external_folder_path ?? activeSyncMapping?.external_folder_id ?? null,
    [activeSyncMapping]
  )
  const matterOverviewPath = matterId ? matterPaths.overview(matterId) : '/dashboard/matters'
  const driveSyncEnabled = kGetFeatureFlagStatus(kFeatureFlags.DRIVE_SYNC)
  const loading =
    accountsRequest.status === kExternalStorageRequestStatus.IDLE ||
    accountsRequest.status === kExternalStorageRequestStatus.LOADING ||
    (!!matterId && (mappingRequest.status === kExternalStorageRequestStatus.IDLE || mappingRequest.status === kExternalStorageRequestStatus.LOADING))
  const error =
    accountsRequest.status === kExternalStorageRequestStatus.ERROR || mappingRequest.status === kExternalStorageRequestStatus.ERROR
      ? 'Failed to load connected accounts or sync settings.'
      : null

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex h-[60px] shrink-0 items-center gap-1 border-b border-brand-neutral-200 px-3">
        <Link to={matterOverviewPath} className="rounded-md px-2 py-1 text-sm font-semibold text-brand-neutral-600 hover:bg-brand-neutral-100">
          {matter?.name ?? 'Matter'}
        </Link>
        <ChevronRight className="h-5 w-5 text-brand-neutral-600" />
        <span className="rounded-md px-2 py-1 text-sm font-semibold text-brand-neutral-950">Matter Settings</span>
      </div>

      <div className="flex min-h-0 flex-1 items-start justify-center overflow-y-auto px-4 py-6 lg:px-10">
        <MatterSettingsConnectedAccountCard
          driveSyncEnabled={driveSyncEnabled}
          error={error}
          loading={loading}
          canChooseFolder={canChooseFolder}
          disconnectingFolder={disconnectingFolder}
          syncedFolderName={syncedFolderName}
          syncAccount={syncAccount}
          onChooseFolder={() => setSelectFolderOpen(true)}
          onDisconnectFolder={() => setDisconnectFolderDialogOpen(true)}
        />
      </div>

      {matterId && syncAccount && canChooseFolder && (
        <SelectExternalFolderModal
          account={syncAccount}
          currentExternalFolderId={syncMapping?.external_folder_id}
          matterId={matterId}
          open={selectFolderOpen}
          onOpenChange={setSelectFolderOpen}
          replaceExistingMapping={syncMapping != null}
          onSuccess={() => {
            handleFolderImportSuccess()
          }}
        />
      )}

      {syncAccount && syncedFolderName && (
        <DisconnectDialog
          open={disconnectFolderDialogOpen}
          loading={disconnectingFolder}
          title="Disconnect synced folder?"
          description={`This will stop syncing ${syncedFolderName} from ${
            kExternalStorageProviderDisplayName[syncAccount.provider]
          } to this matter. Your document management account will stay connected.`}
          onOpenChange={setDisconnectFolderDialogOpen}
          onConfirm={handleFolderDisconnect}
        />
      )}
    </div>
  )
}
